import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import { GTFonts, PoppinsFonts, useTheme } from '../../../theme';
import { formatWithCommas, s, fs, v, iconSize } from '../../../utils/libs';
import type { ExpenseSummary } from '../../../api/backofficeExpenses';

type ExpenseHeroCardProps = {
  summary: ExpenseSummary | null;
  loading: boolean;
  dateRangeLabel: string;
  onViewExpenses?: () => void;
};

const ExpenseHeroCard: React.FC<ExpenseHeroCardProps> = ({
  summary,
  loading,
  dateRangeLabel,
  onViewExpenses,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const totalFormatted = summary
    ? `KSh ${formatWithCommas(summary.totalExpenses, 0)}`
    : 'KSh 0';

  const prevPeriod = summary?.previousPeriod;
  const changeLabel = prevPeriod
    ? `${prevPeriod.changeDirection === 'up' ? 'Up' : prevPeriod.changeDirection === 'down' ? 'Down' : 'Flat'} ${prevPeriod.changePercent}% from previous period (KSh ${formatWithCommas(prevPeriod.totalExpenses, 0)})`
    : '';

  return (
    <View style={styles.container}>
      {/* Top section: period label + View Expenses CTA */}
      <View style={styles.headerRow}>
        <Text style={styles.periodLabel}>{dateRangeLabel}</Text>
        {onViewExpenses ? (
          <TouchableOpacity
            onPress={onViewExpenses}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            activeOpacity={0.7}
            style={styles.viewExpensesLink}>
            <Text style={styles.viewExpensesLinkText}>View Expenses</Text>
            <MaterialIcons
              name="chevron-right"
              size={iconSize(18)}
              color="rgba(255,255,255,0.95)"
            />
          </TouchableOpacity>
        ) : null}
      </View>
      {loading ? (
        <SkeletonPlaceholder
          backgroundColor="#FFFFFF26"
          highlightColor="#FFFFFF40">
          <SkeletonPlaceholder.Item
            width={s(200)}
            height={v(32)}
            borderRadius={s(4)}
            marginTop={v(4)}
          />
        </SkeletonPlaceholder>
      ) : (
        <Text style={styles.totalAmount}>{totalFormatted}</Text>
      )}

      {/* Comparison line */}
      {loading ? (
        <SkeletonPlaceholder
          backgroundColor="#FFFFFF26"
          highlightColor="#FFFFFF40">
          <SkeletonPlaceholder.Item
            width={s(180)}
            height={v(14)}
            borderRadius={s(4)}
            marginTop={v(6)}
          />
        </SkeletonPlaceholder>
      ) : prevPeriod && prevPeriod.changeDirection !== 'flat' ? (
        <Text style={styles.comparisonText}>{changeLabel}</Text>
      ) : null}

      {/* Divider */}
      <View style={styles.divider} />

      {/* Footer stats row */}
      <View style={styles.statsRow}>
        <HeroStat
          label="Manual"
          value={loading ? null : String(summary?.manualCount ?? 0)}
          sub="entries"
          loading={loading}
          theme={theme}
        />
        <HeroStat
          label="Auto-recorded"
          value={loading ? null : String(summary?.autoRecordedCount ?? 0)}
          sub="from stock-in"
          loading={loading}
          theme={theme}
        />
        <HeroStat
          label="Expense"
          value={loading ? null : String(summary?.activeCategoriesCount ?? 0)}
          sub="categories"
          loading={loading}
          theme={theme}
        />
      </View>
    </View>
  );
};

type HeroStatProps = {
  label: string;
  value: string | null;
  sub: string;
  loading: boolean;
  theme: any;
};

const HeroStat: React.FC<HeroStatProps> = ({
  label,
  value,
  sub,
  loading,
  theme,
}) => {
  const styles = useMemo(() => createStyles(theme), [theme]);
  return (
    <View style={styles.statItem}>
      <Text style={styles.statLabel}>{label}{'\n'}{sub}</Text>
 
      {loading ? (
        <SkeletonPlaceholder
          backgroundColor="#FFFFFF26"
          highlightColor="#FFFFFF40">
          <SkeletonPlaceholder.Item
            width={s(28)}
            height={v(20)}
            borderRadius={s(4)}
            marginTop={v(2)}
          />
        </SkeletonPlaceholder>
      ) : (
        <Text style={styles.statValue}>{value}</Text>
      )}
   
    </View>
  );
};

export default ExpenseHeroCard;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.successEmerald,
      borderRadius: theme.borderRadius.md,
      padding: s(12),
      marginHorizontal: s(5),
      marginTop: v(5),
    },
    headerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: v(2),
    },
    periodLabel: {
      fontSize: fs(12),
      fontFamily: PoppinsFonts.regular,
      color: theme.colors.whitesmoke,
      flexShrink: 1,
    },
    viewExpensesLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(2),
      paddingVertical: v(2),
    },
    viewExpensesLinkText: {
      fontWeight: '600',
      fontSize: fs(14),
      lineHeight: fs(16),
      color: 'rgba(255,255,255,0.95)',
      textDecorationLine: 'underline',
      textDecorationColor: 'rgba(255,255,255,0.95)',
    },
    totalAmount: {
      fontSize: fs(28),
      fontFamily: GTFonts.bold,
      color: '#FFFFFF',
      lineHeight: fs(34),
    },
    comparisonText: {
      fontSize: fs(12),
      fontFamily: PoppinsFonts.regular,
      color: theme.colors.whitesmoke,
      marginTop: v(4),
    },
    divider: {
      height: 1,
      backgroundColor: theme.colors.whitesmoke,
      marginTop: v(14),
      marginBottom: v(12),
    },
    statsRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    statItem: {
      flex: 1,
    },
    statLabel: {
      fontSize: fs(10),
      fontFamily: PoppinsFonts.regular,
      color: theme.colors.whitesmoke,
      marginBottom: v(2),
    },
    statValue: {
      fontSize: fs(20),
      fontFamily: GTFonts.bold,
      color: '#FFFFFF',
      lineHeight: fs(24),
    },
    statSub: {
      fontSize: fs(10),
      fontFamily: PoppinsFonts.regular,
      color: theme.colors.whitesmoke,
    },
  });
